import React, { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Chip,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import ResourceNotFound from 'components/ResourceNotFound'
import Wrapper from 'components/Wrapper'
import getTableOptions from 'utils/getTableOptions'
import attendeeSlice from 'store/attendee'
import BasicTable from 'components/BasicTable'
import { Box } from '@mui/material'
import { AttendeeRoles, Collection, orgRepresentativeStatus } from 'constants/index'
import {
  EditOutlined as EditIcon,
  Close as CloseIcon,
  Notifications,
  Email,
  DeleteOutlined as DeleteIcon
} from '@material-ui/icons'
import { renderCol } from 'utils/renderColumns'
import { useHistory, useLocation } from 'react-router-dom'
import { toast } from 'utils/notify'
import ProgressButton from 'components/ProgressButton'
import { cloudFunctionsInstance } from 'config/axios'
import { FirestoreDatabase } from '../../../../index'
import { caseInsensitiveSort } from 'utils'
import { chipStyleForOrgRepresentative } from '../../organizations/utils'
import SendCustomEmailModal from './SendCustomEmailModal'
import { useAttendeeListingStyles } from './styles/attendeeListing'

// Added roleFilter prop
const AttendeesListing = () => {
  const classes = useAttendeeListingStyles()
  const dispatch = useDispatch()
  const location = useLocation()
  const history = useHistory()
  // const [roleFilter, setRoleFilter] = useState('all')
  const [selectedRows, setSelectedRows] = useState([])
  const [isNotifying, setIsNotifying] = useState(false)
  const [confirmNotifyModal, setConfirmNotifyModal] = useState(false)
  const [customEmailModalOpen, setCustomEmailModalOpen] = useState(false)
  const [fixedAttendeeForEmail, setFixedAttendeeForEmail] = useState(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [attendeeToDelete, setAttendeeToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const { user } = useSelector((state) => state.auth)
  const { attendees, roleFilter, currentPage, pageSize, inProgress } = useSelector(
    (state) => state.attendee
  )

  const setRoleFilter = (role) => {
    dispatch(attendeeSlice.actions.setRoleFilter(role))
  }

  const getAttendees = (conferenceId) => {
    return FirestoreDatabase.collection(Collection.ATTENDEES)
      .where('conferenceId', '==', conferenceId)
      .onSnapshot((snapshot) => {
        const attendees = snapshot.docs?.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }))
        dispatch(attendeeSlice.actions.setAttendees(caseInsensitiveSort(attendees, 'firstName')))
      })
  }

  useEffect(() => {
    const unsub = getAttendees(user?.conferenceId)
    return () => unsub()
  }, [dispatch, user])

  // Statistics for attendee statuses
  const attendeeStats = useMemo(() => {
    const stats = {
      [AttendeeRoles.ATTENDEE]: 0,
      [AttendeeRoles.SPEAKER]: 0,
      [AttendeeRoles.MODERATOR]: 0,
      [AttendeeRoles.REPRESENTATIVE]: 0
    }

    attendees?.forEach((attendee) => {
      const role = attendee?.role || AttendeeRoles.ATTENDEE
      if (Object.prototype.hasOwnProperty.call(stats, role)) {
        stats[role]++
      } else {
        stats[AttendeeRoles.ATTENDEE]++
      }
    })

    return stats
  }, [attendees])

  // Filter attendees by role and status
  const filteredAttendees = useMemo(() => {
    let filtered = attendees

    // Filter by role
    if (roleFilter !== 'all') {
      filtered = filtered.filter(
        (attendee) => (attendee.role || AttendeeRoles.ATTENDEE) === roleFilter
      )
    }

    return filtered
  }, [attendees, roleFilter])

  const clearFilters = () => {
    setRoleFilter('all')
  }

  const tableHead = [
    'First Name',
    'Last Name',
    'Email',
    'Credential',
    'Position',
    'Organization',
    'Role',
    {
      name: 'Status',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => renderCol(value),
        customHeadLabelRender: (meta) => renderCol(meta.label)
      }
    },
    {
      name: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => renderCol(value),
        customHeadLabelRender: (meta) => (
          <div style={{ textAlign: 'center' }}>{renderCol(meta.label)}</div>
        )
      }
    }
  ]

  const formatTableData = (attendees) =>
    attendees.map((attendee) => [
      attendee?.firstName,
      attendee?.lastName,
      attendee?.email,
      attendee?.credential,
      attendee?.position,
      attendee?.company,
      <Box component="span" className={classes.flexBox}>
        <Typography variant="body2">{attendee?.role ?? 'Attendee'}</Typography>
      </Box>,
      <Chip
        size="small"
        label={
          attendee?.status === orgRepresentativeStatus.REGISTERED
            ? 'Registered'
            : attendee?.status === orgRepresentativeStatus.NOTIFIED
            ? 'Notified'
            : 'Not Notified'
        }
        style={{
          ...chipStyleForOrgRepresentative(attendee?.status).style
        }}
      />,
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
        <Tooltip title="Send Custom Email">
          <IconButton
            size="small"
            onClick={() => {
              setFixedAttendeeForEmail(attendee)
              setCustomEmailModalOpen(true)
            }}>
            <Email fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit">
          <IconButton
            size="small"
            onClick={() => history.push(`${location.pathname}/attendees/edit/${attendee.id}`)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" onClick={() => handleDeleteClick(attendee)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>
    ])

  // Handle row selection
  const handleRowSelectionChange = (currentRowsSelected, allRowsSelected) => {
    const selected = allRowsSelected.map((row) => row.dataIndex)
    setSelectedRows(selected)
  }

  const options = {
    ...getTableOptions(),
    selectableRows: 'multiple',
    selectableRowsOnClick: false,
    selectableRowsHeader: true,
    selectableRowsHideCheckboxes: false,
    onRowSelectionChange: handleRowSelectionChange,
    rowsSelected: selectedRows.map((dataIndex) =>
      attendees.findIndex((_item, idx) => idx === dataIndex)
    ),
    customToolbarSelect: () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '8px 12px' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Notifications />}
          onClick={handleNotifySelected}
          disabled={isNotifying}>
          Notify Attendees
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Email />}
          onClick={() => setCustomEmailModalOpen(true)}>
          Send Custom Email
        </Button>
      </Box>
    )
  }

  // Handle notification for selected attendees
  const handleNotifySelected = async () => {
    if (!selectedRows.length) {
      toast.warning('No attendees selected.')
      return
    }

    setConfirmNotifyModal(true)
  }

  // Send notification to selected attendees
  const sendNotifications = async () => {
    try {
      setIsNotifying(true)
      const selectedAttendees = selectedRows.map((index) => filteredAttendees[index])

      const response = await cloudFunctionsInstance.post('/notifyRegularAttendees', {
        attendeeIds: selectedAttendees.map((attendee) => attendee.id),
        conferenceId: user?.conferenceId
      })
      const results = response.data
      const notifiedCount = results.recipients.length || 0
      const registeredCount = results.registered || 0
      toast.success(`${notifiedCount - registeredCount} attendee(s) notified.`)
      registeredCount &&
        toast.warning(
          `${registeredCount} attendee(s) were already registered and did not receive a notification email.`
        )
      setConfirmNotifyModal(false)
      setSelectedRows([])
    } catch (error) {
      toast.error('Could not send notifications. Try again.')
      console.error(error)
    } finally {
      setIsNotifying(false)
    }
  }

  const handleDeleteClick = (attendee) => {
    setAttendeeToDelete(attendee)
    setDeleteConfirmOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!attendeeToDelete) return
    try {
      setIsDeleting(true)
      await FirestoreDatabase.collection(Collection.ATTENDEES).doc(attendeeToDelete.id).delete()

      const deviceSnap = await FirestoreDatabase.collection(Collection.ATTENDEE_DEVICE)
        .where('attendeeId', '==', attendeeToDelete.id)
        .get()
      const deletions = deviceSnap.docs.map((doc) => doc.ref.delete())
      await Promise.all(deletions)

      toast.success('Attendee deleted.')
      setDeleteConfirmOpen(false)
      setAttendeeToDelete(null)
    } catch (error) {
      toast.error('Could not delete attendee. Try again.')
      console.error(error)
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <>
      {/* Attendee Stats Filter Bar */}
      <div className={classes.filterStatsBar}>
        <div className={classes.statsChips}>
          <div className={classes.statsSection}>
            <div className={classes.statChipsContainer}>
              <Chip
                label={`All: ${attendees?.length || 0}`}
                className={roleFilter === 'all' ? classes.activeChip : classes.statChip}
                onClick={() => {
                  setSelectedRows([])
                  setRoleFilter('all')
                }}
              />
              {Object.entries(AttendeeRoles).map(([key, value]) => (
                <Chip
                  key={key}
                  label={`${value}: ${attendeeStats[value] || 0}`}
                  className={roleFilter === value ? classes.activeChip : classes.statChip}
                  onClick={() => {
                    setSelectedRows([])
                    setRoleFilter(value)
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {roleFilter !== 'all' && (
          <Button
            variant="outlined"
            color="primary"
            size="medium"
            className={classes.clearButton}
            onClick={clearFilters}
            startIcon={<CloseIcon />}>
            Clear Filters
          </Button>
        )}
      </div>

      <Wrapper inProgress={inProgress}>
        {attendees && attendees.length ? (
          filteredAttendees.length > 0 ? (
            <BasicTable
              data={formatTableData(filteredAttendees)}
              columns={tableHead}
              options={{
                ...options,
                onChangePage: (page) => dispatch(attendeeSlice.actions.setCurrentPage(page)),
                onChangeRowsPerPage: (size) => dispatch(attendeeSlice.actions.setPageSize(size)),
                page: currentPage,
                rowsPerPage: pageSize
              }}
            />
          ) : (
            <ResourceNotFound
              message={roleFilter !== 'all' && `No attendees with role "${roleFilter}" available`}
            />
          )
        ) : (
          !inProgress && <ResourceNotFound message="No attendee available for this conference" />
        )}
      </Wrapper>

      {/* Notify Attendees Confirmation Modal */}
      <Dialog
        open={confirmNotifyModal}
        onClose={() => !isNotifying && setConfirmNotifyModal(false)}
        aria-labelledby="notify-attendees-dialog-title"
        aria-describedby="notify-attendees-dialog-description">
        <DialogTitle id="notify-attendees-dialog-title">Notify Attendees</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {`Are you sure you want to notify the selected ${selectedRows.length} attendee${
              selectedRows.length !== 1 ? 's' : ''
            }?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmNotifyModal(false)}
            color="primary"
            variant="outlined"
            size="medium"
            autoFocus>
            Cancel
          </Button>
          <ProgressButton
            inProgress={isNotifying}
            onClick={sendNotifications}
            label={isNotifying ? 'Sending...' : 'Notify'}
            disabled={isNotifying}
            color="primary"
            size="medium">
            Notify
          </ProgressButton>
        </DialogActions>
      </Dialog>

      {/* Delete Attendee Confirmation Modal */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => !isDeleting && setDeleteConfirmOpen(false)}
        aria-labelledby="delete-attendee-dialog-title"
        maxWidth="xs"
        fullWidth>
        <DialogTitle id="delete-attendee-dialog-title" disableTypography>
          <div className={classes.deleteDialogHeader}>
            <div className={classes.deleteIconCircle}>
              <DeleteIcon style={{ fontSize: 28, color: '#d32f2f' }} />
            </div>
            <Typography variant="h6" className={classes.deleteDialogTitle}>
              Delete Attendee
            </Typography>
            <IconButton
              size="small"
              className={classes.deleteDialogClose}
              onClick={() => !isDeleting && setDeleteConfirmOpen(false)}
              disabled={isDeleting}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent className={classes.deleteDialogContent}>
          <Typography variant="body1">
            Are you sure you want to delete{' '}
            <strong>
              {attendeeToDelete?.lastName}, {attendeeToDelete?.firstName}
            </strong>
            ? This action cannot be undone.
          </Typography>
          {attendeeToDelete?.status === orgRepresentativeStatus.REGISTERED && (
            <div className={classes.deleteWarningBox}>
              <Typography variant="body2">
                This attendee is registered on the mobile app. Deleting will also remove their
                device registration.
              </Typography>
            </div>
          )}
        </DialogContent>
        <DialogActions className={classes.deleteDialogActions}>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            variant="outlined"
            size="large"
            disabled={isDeleting}>
            Cancel
          </Button>
          <ProgressButton
            inProgress={isDeleting}
            onClick={handleConfirmDelete}
            label={isDeleting ? 'Deleting...' : 'Delete'}
            disabled={isDeleting}
            fullWidth>
            Delete
          </ProgressButton>
        </DialogActions>
      </Dialog>

      {/* Send Custom Email Modal */}
      <SendCustomEmailModal
        open={customEmailModalOpen}
        onClose={() => {
          setCustomEmailModalOpen(false)
          setSelectedRows([])
          setFixedAttendeeForEmail(null)
        }}
        selectedRecipients={
          fixedAttendeeForEmail
            ? [fixedAttendeeForEmail]
            : selectedRows.map((index) => filteredAttendees[index])
        }
        availableRecipients={attendees || []}
      />
    </>
  )
}

export default AttendeesListing
